import {
    Box,
    Container,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tabs,
    Tab,
    useMediaQuery,
    useTheme as useMuiTheme,
    Chip,
    Stack,
    IconButton,
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import Footer from '../components/Footer';
import { useThemeContext } from '../context/ThemeContext';
import { useState, useRef, useEffect } from 'react';
import Slider from 'react-slick';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const works = [
    {
        title: 'Robotics Control System',
        description: 'AI-powered robotics software for restaurant automation and warehouse logistics.',
        category: 'Robotics',
        image: '/service_images/services_robotics.jpg',
        technologies: ['React.js', 'Redis', 'Node.js', 'PostgreSQL'],
    },
    {
        title: 'Ecommerce Website',
        description: 'Feature-rich e-commerce solution with payment gateways, cart, admin panel and order tracking.',
        category: 'E-commerce',
        image: '/service_images/service_ecommerce.jpg',
        technologies: ['React.js', 'Node.js', 'MongoDB', 'Redis', "Kafka", "AWS", 'Docker'],
    },
    {
        title: 'Healthcare Website',
        description: 'Appointment booking, doctor profiles, patient dashboards and medical records integrated.',
        category: 'Healthcare',
        image: '/service_images/services_healthcare.jpg',
        technologies: ['Next.js', 'PostgreSQL', 'Express', 'TailwindCSS'],
    },
    {
        title: 'ERP System',
        description: 'Modular ERP system to manage HR, Inventory, Accounting, and CRM with real-time dashboards.',
        category: 'ERP',
        image: '/service_images/services_erp.jpg',
        technologies: ['Laravel', 'Vue.js', 'MySQL', 'Docker'],
    },
    {
        title: 'Automobile Software',
        description: 'Vehicle diagnostics, workshop scheduling, and driver behavior analytics platform.',
        category: 'Automobiles',
        image: '/service_images/services_automobiles.png',
        technologies: ['Flutter', 'Firebase', 'Node.js', 'MongoDB'],
    },
    {
        title: 'Restaurant Management System',
        description: 'POS, order queue, kitchen display and billing system for multi-branch restaurants.',
        category: 'Restaurant',
        image: '/service_images/services_restuarant.png',
        technologies: ['React', 'Express.js', 'MongoDB', 'Socket.io'],
    },
    {
        title: 'Rider Tracking System',
        description: 'Real-time GPS tracking system for delivery agents with route optimization and alerts.',
        category: 'Tracking',
        image: '/service_images/services_rider.jpg',
        technologies: ['React Native', 'Mapbox', 'Node.js', 'Redis'],
    },
    {
        title: 'HR Portal',
        description: 'Employee management, leave tracking, payroll, and self-service portal.',
        category: 'HR',
        image: '/service_images/hr_portal_services.png',
        technologies: ['Angular', 'Spring Boot', 'PostgreSQL'],
    },
    {
        title: 'Music Application',
        description: 'Stream music, create playlists, discover artists — powered by modern backend APIs.',
        category: 'Music',
        image: '/service_images/service_music.jpg',
        technologies: ['React Native', 'GraphQL', 'Node.js', 'MongoDB'],
    },
];

const bannerImages = [
    '/service_images/services1.jpg',
    '/service_images/services2.png'
];



const categories = ['All', ...new Set(works.map((w) => w.category))];
console.log(categories, " categories");

const Arrow = ({ onClick, direction }) => (
    <IconButton
        onClick={onClick}
        sx={{
            position: 'absolute',
            top: '45%',
            [direction]: 0,
            zIndex: 3,
            backgroundColor: '#fff',
            color: '#000',
            '&:hover': {
                backgroundColor: '#000',
                color: '#fff',
            },
        }}
    >
        {direction === 'left' ? <ArrowBackIos /> : <ArrowForwardIos />}
    </IconButton>
);

export default function Works() {
    const { theme } = useThemeContext();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

    const [selectedCategory, setSelectedCategory] = useState('All');
    const [openDialog, setOpenDialog] = useState(false);
    const [activeWork, setActiveWork] = useState(null);

    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const tabsRef = useRef();
    const sliderRef = useRef(); // ✅ Added for future use, if needed

    const filteredWorks =
        selectedCategory === 'All'
            ? works
            : works.filter((work) => work.category === selectedCategory);
    console.log(filteredWorks, " filtered works");
    console.log(filteredWorks.length, " filtered works.length")

    const handleOpenDialog = (work) => {
        setActiveWork(work);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setActiveWork(null);
    };

    const scrollTabs = (dir) => {
        if (tabsRef.current) {
            const scrollAmount = 120;
            tabsRef.current.scrollLeft += dir === 'left' ? -scrollAmount : scrollAmount;
        }
    };

    const updateScrollButtons = () => {
        const el = tabsRef.current;
        if (!el) return;

        const { scrollLeft, scrollWidth, clientWidth } = el;
        const hasScroll = scrollWidth > clientWidth + 2; // small buffer
        setCanScrollLeft(hasScroll && scrollLeft > 0);
        setCanScrollRight(hasScroll && scrollLeft + clientWidth < scrollWidth - 2);
    };


    // On scroll and resize
    useEffect(() => {
        const el = tabsRef.current;
        if (!el) return;

        updateScrollButtons();

        el.addEventListener('scroll', updateScrollButtons);
        window.addEventListener('resize', updateScrollButtons);

        return () => {
            el.removeEventListener('scroll', updateScrollButtons);
            window.removeEventListener('resize', updateScrollButtons);
        };
    }, []);

    // On tab/category change
    useEffect(() => {
        setTimeout(updateScrollButtons, 150);
    }, [selectedCategory, isMobile]);



    const [currentSlide, setCurrentSlide] = useState(0);
    const [paused, setPaused] = useState(false);


    const bannerSettings = {
        infinite: true,
        autoplay: true,
        autoplaySpeed: 6000,
        speed: 1000,
        fade: true,
        arrows: false,
        dots: false, // We are replacing this with custom progress bars
        beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    };


    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: isMobile ? 1 : 2,
        slidesToScroll: 1,
        arrows: true,
        prevArrow: <Arrow direction="left" />,
        nextArrow: <Arrow direction="right" />,
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.background,
                color: theme.text,
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{ position: 'relative', mb: 6 }}>
                <Slider
                    {...bannerSettings}
                    beforeChange={(oldIndex, newIndex) => {
                        setCurrentSlide(newIndex);
                    }}
                >
                    {bannerImages.map((img, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                width: '100%',
                                height: { xs: '80vh', sm: '90vh' },
                                backgroundImage: `url(${img})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}
                        />
                    ))}
                </Slider>

                {/* Overlay & Text */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                    }}
                >
                    {/* Dark Overlay */}
                    <Box
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            zIndex: 1,
                        }}
                    />

                    {/* Main Text */}
                    <Typography
                        variant="h2"
                        sx={{
                            zIndex: 2,
                            fontWeight: 900,
                            textAlign: 'center',
                            px: 2,
                            color: '#fff',
                            fontSize: { xs: '3.5rem', sm: '4.5rem' },
                        }}
                    >
                        Our Services
                    </Typography>

                    {/* Progress Bars */}
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 5, zIndex: 2 }}>
                        {bannerImages.map((_, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    height: 4,
                                    width: 40,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    backgroundColor: `${theme.headerFooter}33`,
                                    position: 'relative',
                                }}
                            >
                                {currentSlide === idx && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            height: '100%',
                                            width: '100%',
                                            backgroundColor: theme.headerFooter,
                                            animation: paused ? 'none' : 'progressBar 5s linear',
                                        }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Animation keyframes */}
                <style>
                    {`
                @keyframes progressBar {
                    from { width: 0%; }
                    to { width: 100%; }
                }
            `}
                </style>
            </Box>


            <Box sx={{ flex: 1 }}>
                <Container maxWidth="lg">
                    <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 4 }}>
                        We utilize a modern tech stack including Node.js, React, Java, HTML, and CSS to build dynamic, responsive, and scalable applications. For cloud infrastructure and storage, we rely on trusted platforms like AWS and Microsoft Azure to ensure security, reliability, and scalability.
                        To ensure a smooth transition post-delivery, we offer free maintenance and support for an initial period—giving you the time and guidance needed to become familiar with the system.
                        With HOT, you're not just getting a product—you’re getting a committed team focused on building solutions that grow with your business.
                        Here are some Projects we've built across diverse industries — Robotics, Healthcare, Ecommerce, and more.
                    </Typography>

                    {/* Filter Tabs with scrollable arrows */}
                    <Box
                        sx={{
                            mb: 4,
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {/* Left Arrow */}
                        <IconButton
                            onClick={() => scrollTabs('left')}
                            sx={{
                                opacity: canScrollLeft ? 1 : 0.3,
                                pointerEvents: canScrollLeft ? 'auto' : 'none',
                                transition: 'opacity 0.3s',
                            }}
                        >
                            <ArrowBackIos />
                        </IconButton>

                        {/* Tabs */}
                        <Box
                            ref={tabsRef}
                            sx={{
                                overflowX: 'auto',
                                flex: 1,
                                whiteSpace: 'nowrap',
                                scrollbarWidth: 'none',
                                '&::-webkit-scrollbar': {
                                    display: 'none',
                                },
                            }}
                        >
                            <Tabs
                                value={selectedCategory}
                                onChange={(e, newVal) => {
                                    setSelectedCategory(newVal);
                                    setTimeout(updateScrollButtons, 100); // allow tabs to render
                                }}
                                variant="scrollable"
                                scrollButtons={false}
                                textColor="inherit"
                                indicatorColor="secondary"
                                sx={{
                                    '& .MuiTab-root': { fontWeight: 500, whiteSpace: 'nowrap' },
                                    '& .MuiTabs-indicator': { backgroundColor: theme.text },
                                }}
                            >
                                {categories.map((cat) => (
                                    <Tab key={cat} label={cat} value={cat} />
                                ))}
                            </Tabs>
                        </Box>

                        {/* Right Arrow */}
                        <IconButton
                            onClick={() => scrollTabs('right')}
                            sx={{
                                opacity: canScrollRight ? 1 : 0.3,
                                pointerEvents: canScrollRight ? 'auto' : 'none',
                                transition: 'opacity 0.3s',
                            }}
                        >
                            <ArrowForwardIos />
                        </IconButton>
                    </Box>


                    {/* ✅ Fixed: Project Cards Slider with key */}
                    {filteredWorks.length > 2 ? (
                        <Slider {...sliderSettings} ref={sliderRef}>
                            {filteredWorks.map((work) => (
                                <Box key={work.title} px={2}>
                                    <ProjectCard work={work} onClick={() => handleOpenDialog(work)} theme={theme} muiTheme={muiTheme} />
                                </Box>
                            ))}
                        </Slider>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: 4,
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                                px: 2,
                            }}
                        >
                            {filteredWorks.map((work) => (
                                <Box key={work.title} sx={{ flex: '0 1 45%' }}>
                                    <ProjectCard work={work} onClick={() => handleOpenDialog(work)} theme={theme} muiTheme={muiTheme} />
                                </Box>
                            ))}
                        </Box>
                    )}


                </Container>
            </Box>

            {/* Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                {activeWork && (
                    <>
                        <DialogTitle>{activeWork.title}</DialogTitle>
                        <DialogContent dividers>
                            {activeWork.image && (
                                <Box
                                    component="img"
                                    src={activeWork.image}
                                    alt={activeWork.title}
                                    width="100%"
                                    borderRadius={2}
                                    mb={2}
                                />
                            )}
                            <Typography variant="body1" paragraph>
                                {activeWork.description}
                            </Typography>
                            <Typography variant="subtitle1" gutterBottom>
                                Technologies Used:
                            </Typography>
                            {activeWork.technologies?.length ? (
                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                    {activeWork.technologies.map((tech, idx) => (
                                        <Chip key={idx} label={tech} color="primary" />
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Not listed.
                                </Typography>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Footer />
        </Box>
    );
}

const ProjectCard = ({ work, onClick, theme, muiTheme }) => (
    <Box
        onClick={onClick}
        sx={{
            maxWidth: 450,
            height: 260,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            boxShadow: 4,
            backgroundColor:
                muiTheme.palette.mode === 'dark' ? '#1e1e1e' : '#fff',
            color: theme.text,
            cursor: 'pointer',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 10,
            },
        }}
    >
        <Box sx={{ height: 160 }}>
            <img
                src={work.image}
                alt={work.title}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                }}
            />
        </Box>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
            <Typography variant="h6" noWrap>
                {work.title}
            </Typography>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
            >
                {work.description}
            </Typography>
        </Box>
    </Box>
);

